/**
 * Modal para registrar un gasto
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2, TrendingDown, DollarSign } from "lucide-react";
import { useCreateExpense } from "@/hooks/useFinance";
import { useClub } from "@/hooks/useOrganization";
import { useAuthStore } from "@/stores/authStore";
import { Timestamp } from "firebase/firestore";
import { todayLocalDateString } from "@/lib/utils";
import { toJsDate } from "@/lib/services/activitySubmissions";
import type { Expense } from "@/types";

const expenseSchema = z.object({
  category: z.string().min(1, "Selecciona una categoría"),
  description: z
    .string()
    .min(3, "La descripción debe tener al menos 3 caracteres"),
  amount: z.number().min(1, "El monto debe ser mayor a 0"),
  vendor: z.string().optional(),
  expenseDate: z.string().min(1, "La fecha es requerida"),
  receiptNumber: z.string().optional(),
});

type ExpenseForm = z.infer<typeof expenseSchema>;

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
  expense?: Expense | null;
}

const expenseCategories = [
  { value: "materiales", label: "📦 Materiales y Suministros" },
  { value: "uniformes", label: "👕 Uniformes" },
  { value: "transporte", label: "🚌 Transporte" },
  { value: "alimentacion", label: "🍔 Alimentación" },
  { value: "alquiler", label: "🏠 Alquiler de Espacios" },
  { value: "equipos", label: "⛺ Equipos de Campamento" },
  { value: "impresiones", label: "🖨️ Impresiones y Papelería" },
  { value: "premiaciones", label: "🏆 Premios y Reconocimientos" },
  { value: "capacitacion", label: "📚 Capacitación" },
  { value: "seguros", label: "🛡️ Seguros" },
  { value: "servicios", label: "💡 Servicios Públicos" },
  { value: "otro", label: "📝 Otro" },
];

export default function ExpenseModal({
  isOpen,
  onClose,
  clubId,
  periodId,
  expense,
}: ExpenseModalProps) {
  const isEditing = !!expense;

  const createMutation = useCreateExpense();
  const { data: club } = useClub(clubId);
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ExpenseForm>({
    resolver: zodResolver(expenseSchema),
    defaultValues: expense
      ? {
          category: expense.category,
          description: expense.description,
          amount: expense.amount,
          vendor: expense.vendor || "",
          expenseDate: (toJsDate(expense.expenseDate) ?? new Date())
            .toISOString()
            .split("T")[0],
          receiptNumber: expense.receiptNumber || "",
        }
      : {
          amount: 0,
          expenseDate: todayLocalDateString(),
        },
  });

  const onSubmit = async (data: ExpenseForm) => {
    if (!user) return;

    const expenseData = {
      clubId,
      periodId,
      category: data.category,
      description: data.description,
      amount: data.amount,
      currency: "COP",
      vendor: data.vendor || "",
      expenseDate: Timestamp.fromDate(new Date(data.expenseDate + "T12:00:00")),
      receiptNumber: data.receiptNumber || "",
      status: "valid" as const,
      registeredBy: user.uid,
      registeredByName: profile?.displayName || user.displayName || "Sistema",
    };

    try {
      await createMutation.mutateAsync(expenseData);
      reset();
      onClose();
    } catch (error) {
      console.error("Error creating expense:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-md shadow-red-500/25">
              <TrendingDown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? "Editar Gasto" : "Nuevo Gasto"}
              </h2>
              <p className="text-sm text-gray-500">{club?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Categoría */}
          <Select
            label="Categoría *"
            {...register("category")}
            error={errors.category?.message}
          >
            <option value="">Seleccionar categoría</option>
            {expenseCategories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </Select>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción *
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300"
              placeholder="Detalle del gasto..."
            />
            {errors.description && (
              <p className="text-sm text-red-500 mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Monto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto (COP) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                {...register("amount", { valueAsNumber: true })}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300"
                placeholder="0"
              />
            </div>
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {errors.amount.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Proveedor */}
            <Input
              label="Proveedor/Vendedor"
              {...register("vendor")}
              error={errors.vendor?.message}
              placeholder="Nombre del proveedor"
            />

            {/* Número de recibo */}
            <Input
              label="No. de Recibo"
              {...register("receiptNumber")}
              error={errors.receiptNumber?.message}
              placeholder="Factura/Recibo"
            />
          </div>

          {/* Fecha */}
          <DatePickerField
            control={control}
            name="expenseDate"
            label="Fecha del Gasto *"
            error={errors.expenseDate?.message}
          />

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="bg-red-600 hover:bg-red-700 transition-colors duration-200"
            >
              {isSubmitting || createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 mr-2" />
                  {isEditing ? "Guardar Cambios" : "Registrar Gasto"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
